import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated! Please sign in.");
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed. Request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center p-5">
      <div className="grain" />
      <div className="hero-glow absolute top-0 inset-x-0 h-[500px] z-0 pointer-events-none" />
      <div className="relative z-10 w-full max-w-md rounded-3xl border border-border bg-surface p-8">
        <div className="flex justify-center mb-6"><Logo to="/" /></div>
        <h1 className="font-heading font-black text-2xl tracking-tight text-center mb-2">Choose a new password</h1>
        {!token ? (
          <p className="text-sm text-muted-foreground text-center" data-testid="reset-invalid">This reset link is missing its token. Please request a new one from the sign-in screen.</p>
        ) : (
          <div className="space-y-4 mt-6">
            <div>
              <Label className="mb-2 block">New password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" type="password" placeholder="At least 8 characters" className="bg-background border-border h-12 pl-9" />
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Confirm password</Label>
              <div className="relative">
                <Lock className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" type="password" placeholder="Re-enter password" className="bg-background border-border h-12 pl-9"
                  onKeyDown={(e) => e.key === "Enter" && submit()} />
              </div>
            </div>
            <Button onClick={submit} disabled={busy} data-testid="reset-submit-btn" className="w-full h-12 rounded-full bg-brand-gradient text-white font-bold">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
